import { type Ref, watch } from 'vue'

import clipboardSvgLight from '../assets/icons/light/icon-copy.svg?raw'
import clipboardSvgDark from '../assets/icons/dark/icon-copy.svg?raw'
import checkSvgLight from '../assets/icons/light/icon-check.svg?raw'
import checkSvgDark from '../assets/icons/dark/icon-check.svg?raw'
import { useAppStore } from '../stores/appStore'
import { DisplayMode } from '../types/app/displayMode'

type Options = {
  copyFeedbackMs?: number
  iconHTML?: string
  successIconHTML?: string
}

export function useCodeCopyButtons(containerEl: Ref<HTMLElement | null>, opts: Options = {}) {
  const appStore = useAppStore()
  const {
    copyFeedbackMs = 1200,
    iconHTML = appStore.currentMode() === DisplayMode.Light ? clipboardSvgLight : clipboardSvgDark,
    successIconHTML = appStore.currentMode() === DisplayMode.Light ? checkSvgLight : checkSvgDark,
  } = opts

  const MARK = 'data-code-tools-ready'

  function enhance() {
    const root = containerEl.value
    if (!root) return

    const codeBlocks = root.querySelectorAll<HTMLElement>('pre > code')

    codeBlocks.forEach(codeEl => {
      const pre = codeEl.closest('pre') as HTMLElement | null
      if (!pre || pre.hasAttribute(MARK)) return

      pre.setAttribute(MARK, '1')

      const wrapper = document.createElement('div')
      wrapper.style.position = 'relative'
      pre.parentElement?.insertBefore(wrapper, pre)
      wrapper.appendChild(pre)

      const btn = document.createElement('button')
      btn.type = 'button'
      btn.innerHTML = iconHTML

      Object.assign(btn.style, {
        position: 'absolute',
        top: '8px',
        right: '8px',
        padding: '6px',
        border: '1px solid rgba(0,0,0,.15)',
        borderRadius: '6px',
        background: 'white',
        cursor: 'pointer',
        opacity: '0',
        transition: 'opacity .15s ease-in-out',
        lineHeight: '0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      } as Partial<CSSStyleDeclaration>)

      // Show on hover
      wrapper.addEventListener('mouseenter', () => (btn.style.opacity = '1'))
      wrapper.addEventListener('mouseleave', () => (btn.style.opacity = '0'))

      // Copy on click (Clipboard API with textarea fallback)
      btn.addEventListener('click', async () => {
        const text = codeEl.textContent ?? ''

        const swapToSuccess = () => {
          const old = btn.innerHTML
          btn.innerHTML = successIconHTML
          setTimeout(() => {
            btn.innerHTML = old || iconHTML
          }, copyFeedbackMs)
        }

        try {
          await navigator.clipboard.writeText(text)
          swapToSuccess()
        } catch {
          const ta = document.createElement('textarea')
          ta.value = text
          ta.style.position = 'fixed'
          ta.style.left = '-9999px'
          document.body.appendChild(ta)
          ta.select()
          document.execCommand('copy')
          document.body.removeChild(ta)
          swapToSuccess()
        }
      })

      wrapper.appendChild(btn)
    })
  }

  function refresh() {
    enhance()
  }

  watch(containerEl, () => {
    refresh()
  })

  return { refresh }
}
